/**
 * Shared UI zoom/pan controller for <rse-player> and hosts (e.g. fullscreen overlay).
 * Pure CSS transform — not stream-engine zoom.
 */
export class RseZoomController {
	static readonly MIN = 1;
	static readonly MAX = 5;
	static readonly STEP = 0.25;

	zoom = RseZoomController.MIN;
	panX = 0;
	panY = 0;
	isPanning = false;

	private panStartX = 0;
	private panStartY = 0;
	private panOriginX = 0;
	private panOriginY = 0;
	private panPointerId: number | null = null;
	private viewportEl: HTMLElement | null = null;
	private fallbackWidth = 0;
	private fallbackHeight = 0;

	get transform(): string {
		return `translate(${this.panX}px, ${this.panY}px) scale(${this.zoom})`;
	}

	get isZoomed(): boolean {
		return this.zoom > RseZoomController.MIN;
	}

	get canZoomIn(): boolean {
		return this.zoom < RseZoomController.MAX;
	}

	get canZoomOut(): boolean {
		return this.zoom > RseZoomController.MIN;
	}

	get percentLabel(): string {
		return `${Math.round(this.zoom * 100)}%`;
	}

	setViewport(el: HTMLElement | null): void {
		this.viewportEl = el;
	}

	setFallbackSize(width: number, height: number): void {
		this.fallbackWidth = width;
		this.fallbackHeight = height;
	}

	zoomIn(event?: Event): void {
		event?.stopPropagation();
		this.zoom = Math.min(
			RseZoomController.MAX,
			Math.round((this.zoom + RseZoomController.STEP) * 100) / 100,
		);
		this.clampPan();
	}

	zoomOut(event?: Event): void {
		event?.stopPropagation();
		this.zoom = Math.max(
			RseZoomController.MIN,
			Math.round((this.zoom - RseZoomController.STEP) * 100) / 100,
		);
		if (this.zoom <= RseZoomController.MIN) {
			this.panX = 0;
			this.panY = 0;
			return;
		}
		this.clampPan();
	}

	reset(event?: Event): void {
		event?.stopPropagation();
		this.zoom = RseZoomController.MIN;
		this.panX = 0;
		this.panY = 0;
		this.endPan();
	}

	onWheel(event: WheelEvent): void {
		event.preventDefault();
		event.stopPropagation();
		if (event.deltaY < 0) {
			this.zoomIn();
		} else if (event.deltaY > 0) {
			this.zoomOut();
		}
	}

	startPan(event: PointerEvent, options?: { blocked?: boolean }): boolean {
		if (!this.isZoomed || event.button !== 0 || options?.blocked) {
			return false;
		}
		event.preventDefault();
		event.stopPropagation();
		this.isPanning = true;
		this.panPointerId = event.pointerId;
		this.panStartX = event.clientX;
		this.panStartY = event.clientY;
		this.panOriginX = this.panX;
		this.panOriginY = this.panY;
		try {
			(event.currentTarget as HTMLElement)?.setPointerCapture?.(event.pointerId);
		} catch {
			// ignore
		}
		return true;
	}

	movePan(event: PointerEvent): boolean {
		if (!this.isPanning || event.pointerId !== this.panPointerId) {
			return false;
		}
		event.preventDefault();
		this.panX = this.panOriginX + (event.clientX - this.panStartX);
		this.panY = this.panOriginY + (event.clientY - this.panStartY);
		this.clampPan();
		return true;
	}

	endPan(event?: PointerEvent): boolean {
		if (!this.isPanning) {
			return false;
		}
		if (event && this.panPointerId !== null && event.pointerId !== this.panPointerId) {
			return false;
		}
		this.isPanning = false;
		this.panPointerId = null;
		return true;
	}

	clampPan(): void {
		if (this.zoom <= RseZoomController.MIN) {
			this.panX = 0;
			this.panY = 0;
			return;
		}
		const rect = this.viewportEl?.getBoundingClientRect();
		const width = rect?.width || this.fallbackWidth;
		const height = rect?.height || this.fallbackHeight;
		const maxX = (width * (this.zoom - 1)) / 2;
		const maxY = (height * (this.zoom - 1)) / 2;
		this.panX = Math.max(-maxX, Math.min(maxX, this.panX));
		this.panY = Math.max(-maxY, Math.min(maxY, this.panY));
	}
}
